// ---------------------------------------------------------------------------
// descriptionPrompt.js
// Writing instructions for the "YouTube video description" content style:
// a short, scannable description that sits under a review video, instead of
// a long article.
//
// Voice: the video's presenter, first person, conversational.
// First-hand statements (what was tested, what happened, results, what is
// on screen at a timestamp) come ONLY from the owner's evidence fields.
// With no evidence supplied, the description is still helpful but makes no
// testing claims and lists no chapters.
// ---------------------------------------------------------------------------

export const DESCRIPTION_SYSTEM = `You write the description that goes under a product review video, in the voice of the person who made the video. Return ONLY valid JSON. No markdown, no code fences, no explanation.

STYLE (most important):
- Sound like a real creator talking to viewers: first person ("I"), warm, direct, plain words. Short paragraphs and scannable bullets, the way good YouTube descriptions are written.
- The first two sentences matter most (they show before "...more"): answer the viewer's question straight away.
- Be specific and useful. Every line should tell the viewer something concrete about the product, never filler.
- Balanced: say what is good, what is not, and who should skip it.
- No hype words ("game-changer", "revolutionary", "insane", "unlock", "skyrocket") and no pushy sales lines ("Buy Now", "Act Now", "Click Here", "Limited Time", "Don't miss out").
- Never repeat a fact, figure or sentence in two places.

FIRST-HAND EXPERIENCE (strict):
- If OWNER-SUPPLIED EVIDENCE is provided, that is my real hands-on experience: write it in first person ("I set up...", "In my test...", "You can see at 02:08..."), use ONLY the tests, results, numbers, screenshots and timestamps it contains, and make it the heart of the description.
- If there is NO owner evidence, do not write or imply that I tested, used, bought or measured anything, and do not describe what is shown on screen. Say what the review covers instead ("This review looks at...", "I go through what the official site says and what users report").
- Never invent results, numbers, screenshots, timestamps, quotes, ratings or prices.

FACTS:
- The official website is the source of truth: state its features, pricing, terms and figures as facts.
- User opinions come ONLY from the fact sheet's user_reviews; name the kind of source ("BlackHatWorld users", "Trustpilot reviewers"). If there are none, leave user_feedback empty.

FORMAT:
- The product name is written exactly as given. Use the long search keyword at most once; otherwise use the product name.
- NEVER include country names, country codes or geographic regions.
- Spintax is LIGHT: {option1|option2|option3} on short phrases only (2-3 options), about one group every one or two sentences. Never spin whole sentences. No nested braces. Never inside the product name.`;

export const buildDescriptionPrompt = (keyword, product, angle, pageText, concise = false, research = null) => {
  const ctx = research?.factSheet
    ? `\nRESEARCH FACT SHEET (your ONLY source of product facts; built from the official website and real user reviews):\n---\n${research.factSheet}\n---\n`
    : pageText ? `\nOfficial website content (source of truth; state it as fact):\n---\n${pageText.slice(0, 2500)}\n---\n` : '';
  const evidence = research?.evidence ? `\n${research.evidence}\n` : '';
  const length = concise
    ? 'Keep it tight: about 250 words in total for the description.'
    : 'Aim for about 350-500 words in total for the description.';
  return `Write the video description for my review video about "${product}" as JSON. ${length}
Search keyword: "${keyword}"
Angle: "${angle}"
${ctx}${evidence}
DESCRIPTION PARTS (in the order a viewer reads them):
- standfirst: one sentence (max 30 words) summarising the verdict, worded differently from the hook.
- hook: 1-2 sentences that directly answer "Is ${product} worth the money?" with a qualified verdict (e.g. "Yes, if...", "Only if...", "Not unless...") and who it is for. Never open with filler ("In this video", "Welcome back", "Hey guys", "Are you wondering").
- summary: 2 short paragraphs. Paragraph 1: what ${product} is and its biggest strength and biggest limitation. Paragraph 2: what this review covers.
- experience: 1-2 paragraphs in first person describing my hands-on test and what it showed, using ONLY the owner-supplied evidence. Return [] if no owner evidence was supplied.
- takeaways: 4-6 one-line key takeaways, each a concrete fact or finding (not a teaser).
- pros: 3-4 short points. cons: 3 short points.
- pricing: 1-2 sentences on what it costs and any costs beyond the licence.
- who_for: 1-2 sentences on who it suits. who_should_skip: 1-2 sentences on who should pass.
- user_feedback: 2-3 sentences on what real users report, naming the kind of source. "" if the fact sheet has no user reviews.
- verdict: 1-2 sentences with my bottom line.
- faq: 3 quick questions viewers ask, each answered in 1-2 sentences, without repeating earlier lines.
- hashtags: 3-5 hashtags built from the product name and topic (e.g. "#${product.replace(/[^A-Za-z0-9]/g, '')}", "#${product.replace(/[^A-Za-z0-9]/g, '')}Review").

VIDEO CARDS (shown beside the description as "More videos"):
- card_titles: EXACTLY 9 short questions a buyer asks (e.g. "Is ${product} Worth It for Agencies?").
- card_contents: EXACTLY 9 teasers of 30-50 words, card_contents[i] answering card_titles[i], written fresh (never copying a sentence from the description). Topics in order: [0] who it's worth it for, [1] what it does, [2] the standout feature, [3] the main drawbacks, [4] what it really costs, [5] who should skip it, [6] getting started, [7] a common buyer concern, [8] the key question to ask before buying.

PAGE METADATA (short):
- subtitle: a short parenthetical like "(My Honest ${product} Review)"; trailing: a short phrase about its main features.
- meta_description: ~155 chars answering what ${product} is and who it's for. og_description: 2 sentences. twitter_description: 1 sentence. json_ld_description: 2 sentences. hyd_og_description / hyd_twitter_description: reworded versions.
- secondary_video_subtitles: 4 short parenthetical subtitles; secondary_chapter_sets: 4 arrays of 4 short chapter names.

Return ONLY this JSON object, with "description" first:
{"standfirst":"...","description":{"hook":"...","summary":["...","..."],"experience":[],"takeaways":["...","...","...","..."],"pros":["...","...","..."],"cons":["...","...","..."],"pricing":"...","who_for":"...","who_should_skip":"...","user_feedback":"...","verdict":"...","faq":[{"q":"...","a":"..."},{"q":"...","a":"..."},{"q":"...","a":"..."}],"hashtags":["#...","#...","#..."]},"card_titles":["...","...","...","...","...","...","...","...","..."],"card_contents":["...","...","...","...","...","...","...","...","..."],"subtitle":"...","trailing":"...","meta_description":"...","og_description":"...","twitter_description":"...","json_ld_description":"...","hyd_og_description":"...","hyd_twitter_description":"...","secondary_video_subtitles":["...","...","...","..."],"secondary_chapter_sets":[["...","...","...","..."],["...","...","...","..."],["...","...","...","..."],["...","...","...","..."]]}

CRITICAL: Return ONLY valid JSON. No geo terms. No invented first-hand claims.`.trim();
};

/** Normalise the "description" object from the LLM answer. */
export const sanitiseDescription = (raw, str, list) => {
  const d = raw && typeof raw === 'object' ? raw : {};
  return {
    hook: str(d.hook),
    summary: list(d.summary, 3),
    experience: list(d.experience, 3),
    takeaways: list(d.takeaways, 6),
    pros: list(d.pros, 4),
    cons: list(d.cons, 4),
    pricing: str(d.pricing),
    who_for: str(d.who_for),
    who_should_skip: str(d.who_should_skip),
    user_feedback: str(d.user_feedback),
    verdict: str(d.verdict),
    faq: (Array.isArray(d.faq) ? d.faq : [])
      .map((item) => ({ q: str(item?.q), a: str(item?.a) }))
      .filter((item) => item.q && item.a)
      .slice(0, 4),
    hashtags: list(d.hashtags, 5)
      .map((tag) => `#${tag.replace(/[^A-Za-z0-9_]/g, '')}`)
      .filter((tag) => tag.length > 2),
  };
};

/** The description and the cards are present and complete (not cut off). */
export const hasDescriptionCore = (data) => {
  const d = data?.description;
  return Boolean(
    d && typeof d.hook === 'string' && d.hook.trim()
    && Array.isArray(d.summary) && d.summary.length >= 1
    && Array.isArray(d.takeaways) && d.takeaways.length >= 3
    && typeof d.verdict === 'string' && d.verdict.trim()
    && Array.isArray(data.card_titles) && data.card_titles.length >= 6
    && Array.isArray(data.card_contents) && data.card_contents.length >= 6
    && data.card_contents.slice(0, 6).every((text) => typeof text === 'string' && text.trim())
  );
};
